import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScrollableContentLayout } from "../../../components/common";
import InlineAlert from "../../../components/common/InlineAlert";
import { Input } from "../../../components/input";
import { Button } from "../../../components/button";
import {
  RefreshCw,
  X,
  UserX,
  Users as UsersIcon,
  Users,
  TrendingUp,
  UserCheck,
  Upload,
} from "lucide-react";
import {
  useDeactivateUser,
  useGetUserList,
  USER_LIST_PAGE_SIZE,
  useUpdateUser,
  useUserStatistics,
} from "../hooks";
import type { UserFilters, UserItem, UserUpdateRequest } from "../types/user.types";
import { useToast } from "../../../components/toast-container";
import { useTranslation } from "react-i18next";
import { ConfirmationDialog } from "../../../components/confirmation-dialog";
import {
  getUserRoleColor,
  getUserStatusColor,
  UserDetailsModal,
  UserStatisticsCards,
  UsersDataTable,
  type UserStatisticCard,
} from "../components/user-table";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { UserImportDialog } from "../components/UserImportDialog";
import type { UserImportResponse } from "../types/user.types";

export default function UserListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    users,
    loading,
    error,
    filters,
    updateFilters,
    page,
    setPage,
    totalCount,
    refetch: refreshUsers,
  } = useGetUserList();
  const { updateUser } = useUpdateUser();
  const { deactivateUser } = useDeactivateUser();
  const userStatistics = useUserStatistics();

  const { data: userStats, loading: statsLoading, refresh: refreshUserStats } = userStatistics;
  const { addToast } = useToast();
  const [viewUser, setViewUser] = useState<UserItem | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState<UserItem | null>(null);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);

  const hasActiveFilter = useCallback(
    (filterKey: keyof UserFilters) => {
      const value = filters[filterKey];

      if (
        filterKey === "name" ||
        filterKey === "email" ||
        filterKey === "role" ||
        filterKey === "status"
      ) {
        return typeof value === "string" && value.trim() !== "";
      }

      if (filterKey === "join_date" || filterKey === "last_active_at") {
        const range = value as UserFilters["join_date"];
        return Boolean(range.from || range.to);
      }

      if (filterKey === "available_points") {
        const range = value as UserFilters["available_points"];
        return range.min !== null || range.max !== null;
      }

      if (filterKey === "age") {
        return filters.age !== null || filters.date_of_birth !== null;
      }

      return false;
    },
    [filters],
  );

  const hasAnyActiveFilter = useCallback(() => {
    return (Object.keys(filters) as Array<keyof UserFilters>).some((key) => hasActiveFilter(key));
  }, [filters, hasActiveFilter]);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    country_code: "",
    phone_number: "",
    role: "",
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [documentHeight, setDocumentHeight] = useState(0);

  const roleLabels: Record<string, string> = {
    super_admin: t("users.super_admin"),
    admin: t("users.admin"),
    developer: t("users.developer"),
    user: t("users.user"),
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRefreshAll = () => {
    // Clear all filters first
    updateFilters({
      name: "",
      email: "",
      role: "",
      age: null,
      date_of_birth: null,
      status: "",
      join_date: {
        from: "",
        to: "",
      },
      last_active_at: {
        from: "",
        to: "",
      },
      available_points: {
        min: null,
        max: null,
      },
    });

    // Refresh statistics separately (list will refetch via filters change)
    refreshUserStats();
  };

  const handleImported = async (result: UserImportResponse) => {
    await refreshUsers();
    refreshUserStats();
    addToast({
      type: "success",
      title: t("users.import.success_title"),
      message: t("users.import.success", { count: result.usersCreated }),
    });
  };

  // Handle deactivate user
  const handleDeactivateUser = (user: UserItem) => {
    setUserToDeactivate(user);
    setShowDeactivateDialog(true);
  };

  const handleConfirmDeactivate = async () => {
    if (!userToDeactivate) return;

    try {
      setIsDeactivating(true);
      await deactivateUser(userToDeactivate.id);
      await refreshUsers();
      refreshUserStats();

      // Show success toast
      addToast({
        type: "success",
        title: "Success!",
        message: "User has been deactivated successfully",
      });

      // Close dialog and reset state
      setShowDeactivateDialog(false);
      setUserToDeactivate(null);
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(
        error,
        "An error occurred while deactivating the user",
      );

      addToast({
        type: "error",
        title: "Error!",
        message: errorMessage,
      });
    } finally {
      setIsDeactivating(false);
    }
  };

  // Update form when viewUser changes in edit mode
  useEffect(() => {
    if (isEditing && viewUser) {
      setForm({
        first_name: viewUser.first_name || "",
        last_name: viewUser.last_name || "",
        email: viewUser.email || "",
        country_code: viewUser.country_code || "",
        phone_number: viewUser.phone_number || "",
        role: viewUser.role || "",
      });
    }
  }, [isEditing, viewUser]);

  // Reset filters when component mounts to ensure fresh state
  useEffect(() => {
    // Reset filters on mount to ensure clean state
    updateFilters({
      name: "",
      email: "",
      role: "",
      age: null,
      date_of_birth: null,
      status: "",
      join_date: {
        from: "",
        to: "",
      },
      last_active_at: {
        from: "",
        to: "",
      },
      available_points: {
        min: null,
        max: null,
      },
    });
    setPage(1);

    return () => {
      // Cleanup: reset all filters when leaving the page
      updateFilters({
        name: "",
        email: "",
        role: "",
        age: null,
        date_of_birth: null,
        status: "",
        join_date: {
          from: "",
          to: "",
        },
        last_active_at: {
          from: "",
          to: "",
        },
        available_points: {
          min: null,
          max: null,
        },
      });
      setPage(1);
    };
  }, [updateFilters, setPage]);

  // Update document height when modal opens
  useEffect(() => {
    const updateDocumentHeight = () => {
      setDocumentHeight(
        Math.max(
          document.body.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.clientHeight,
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight,
        ),
      );
    };

    if (viewUser || isEditing || showDeactivateDialog) {
      updateDocumentHeight();
      window.addEventListener("resize", updateDocumentHeight);
      window.addEventListener("scroll", updateDocumentHeight);
    }

    return () => {
      window.removeEventListener("resize", updateDocumentHeight);
      window.removeEventListener("scroll", updateDocumentHeight);
    };
  }, [viewUser, isEditing, showDeactivateDialog]);

  const handleCloseEdit = () => {
    setIsEditing(false);
    setViewUser(null);
    setForm({
      first_name: "",
      last_name: "",
      email: "",
      country_code: "",
      phone_number: "",
      role: "",
    });
    setFormError(null);
  };

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validate
    if (!form.first_name || !form.last_name || !form.email || !form.role) {
      setFormError("Please fill in all required fields.");
      return;
    }

    if (!viewUser) return;

    try {
      setIsUpdating(true);

      const updateRequest: UserUpdateRequest = {
        first_name: form.first_name,
        last_name: form.last_name,
        country_code: form.country_code,
        phone_number: form.phone_number,
      };

      await updateUser(viewUser.id, updateRequest);
      await refreshUsers();
      refreshUserStats();

      // Show success toast
      addToast({
        type: "success",
        title: "Success!",
        message: "User has been updated successfully",
      });

      // Close form and reset
      handleCloseEdit();
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(error, "An error occurred while updating the user");

      setFormError(errorMessage);

      addToast({
        type: "error",
        title: "Error!",
        message: errorMessage,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const stats: UserStatisticCard[] = [
    {
      label: t("users.total_users"),
      value: userStats?.total_users || 0,
      icon: Users,
      subtitle: `New this week: ${
        userStats?.new_users ||
        users.filter((u) => {
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          return new Date(u.created_at) > weekAgo;
        }).length
      }`,
    },
    {
      label: t("users.engaged_users_7d"),
      value: userStats?.engaged_users || 0,
      icon: TrendingUp,
    },
    {
      label: t("users.active_accounts"),
      value: userStats?.active_users || 0,
      icon: UserCheck,
    },
    {
      label: t("users.deleted_accounts"),
      value: userStats?.inactive_users || 0,
      icon: UserX,
    },
  ];

  const totalPages = Math.ceil(totalCount / USER_LIST_PAGE_SIZE);

  return (
    <>
      <ScrollableContentLayout
        title={t("users.management")}
        subtitle={t("users.subtitle")}
        // actions={
        //   <Button
        //     className="flex items-center gap-2"
        //     onClick={() => navigate("/users/create")}
        //   >
        //     <UserPlus className="h-4 w-4" />
        //     {t("users.add_new_user")}
        //   </Button>
        // }
        loading={statsLoading}
        headerContent={
          <div>
            <UserStatisticsCards items={stats} />
            <div className="flex items-center justify-between">
              <div className="text-xl font-semibold">{t("users.user_list")}</div>
              <div className="flex items-center gap-2">
                <Button
                  variant="brand-secondary"
                  size="sm"
                  onClick={() => setShowImportDialog(true)}
                  className="flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  {t("users.import.action")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefreshAll}
                  disabled={loading || statsLoading || !hasAnyActiveFilter()}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${loading || statsLoading ? "animate-spin" : ""}`} />
                  {t("ui.refresh")}
                </Button>
              </div>
            </div>
          </div>
        }
      >
        {error && <InlineAlert message={error} />}

        <UsersDataTable
          users={users}
          filters={filters}
          onFiltersChange={updateFilters}
          loading={loading}
          error={error}
          page={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onRetry={refreshUsers}
          onDeactivate={handleDeactivateUser}
          roleLabels={roleLabels}
          getStatusColor={getUserStatusColor}
          getRoleColor={getUserRoleColor}
          labels={{
            name: t("users.name"),
            email: t("users.email"),
            age: t("users.age"),
            joinDate: t("users.join_date"),
            role: t("users.role"),
            status: t("common.status"),
            lastActive: t("users.last_active_at"),
            actions: t("common.actions"),
          }}
        />
      </ScrollableContentLayout>

      <UserImportDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        onImported={handleImported}
      />

      <UserDetailsModal
        user={viewUser && !isEditing ? viewUser : null}
        onClose={() => setViewUser(null)}
      />

      {/* Edit User Modal */}
      {isEditing && viewUser && (
        <div
          className="fixed z-50 flex items-center justify-center bg-black/80"
          style={{
            top: 0,
            left: 0,
            width: "100vw",
            height: documentHeight > 0 ? `${documentHeight}px` : "100vh",
            minHeight: "100vh",
            position: "fixed",
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 w-full max-w-4xl p-0 relative">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              onClick={handleCloseEdit}
            >
              <X className="w-6 h-6" />
            </button>
            <div className="border-b dark:border-gray-700 px-8 pt-6 pb-3 flex items-center gap-3">
              <UsersIcon className="w-8 h-8 text-emerald-500" />
              <h2 className="text-2xl font-bold dark:text-white">{t("users.edit_user")}</h2>
            </div>
            <form
              key={`edit-${viewUser.id}`}
              onSubmit={handleSubmit}
              className="px-8 py-6 grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              <div className="col-span-1 flex flex-col gap-2">
                <label className="font-medium mb-1 dark:text-white">{t("users.first_name")}</label>
                <Input
                  name="first_name"
                  value={form.first_name}
                  onChange={handleFormChange}
                  required
                  className="rounded-xl"
                  disabled={isUpdating}
                />
                <label className="font-medium mb-1 mt-3 dark:text-white">
                  {t("users.last_name")}
                </label>
                <Input
                  name="last_name"
                  value={form.last_name}
                  onChange={handleFormChange}
                  required
                  className="rounded-xl"
                  disabled={isUpdating}
                />
                <label className="font-medium mb-1 mt-3 dark:text-white">{t("users.email")}</label>
                <Input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleFormChange}
                  required
                  className="rounded-xl"
                  disabled={true}
                />
              </div>
              <div className="col-span-1 flex flex-col gap-2">
                <label className="font-medium mb-1 dark:text-white">
                  {t("users.country_code")}
                </label>
                <Input
                  name="country_code"
                  value={form.country_code}
                  onChange={handleFormChange}
                  className="rounded-xl"
                  disabled={isUpdating}
                />
                <label className="font-medium mb-1 mt-3 dark:text-white">
                  {t("users.phone_number")}
                </label>
                <Input
                  name="phone_number"
                  value={form.phone_number}
                  onChange={handleFormChange}
                  className="rounded-xl"
                  disabled={true}
                />
                <label className="font-medium mb-1 mt-3 dark:text-white">{t("users.role")}</label>
                <select
                  name="role"
                  value={form.role}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-xl p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  disabled={isUpdating}
                  required
                >
                  <option value="">{t("users.select_role")}</option>
                  <option value="user">{t("users.user")}</option>
                  <option value="admin">{t("users.admin")}</option>
                  <option value="developer">{t("users.developer")}</option>
                  <option value="super_admin">{t("users.super_admin")}</option>
                </select>
              </div>
              {formError && (
                <div className="col-span-2 text-red-600 dark:text-red-400 text-sm flex items-center gap-2">
                  <X className="w-4 h-4" />
                  {formError}
                </div>
              )}
              <div className="col-span-2 flex justify-end gap-2 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl"
                  onClick={handleCloseEdit}
                  disabled={isUpdating}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  className="bg-emerald-500 text-white rounded-xl"
                  disabled={isUpdating}
                >
                  {isUpdating ? t("users.updating") : t("users.update")}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deactivate User Dialog */}
      <ConfirmationDialog
        isOpen={showDeactivateDialog}
        onClose={() => setShowDeactivateDialog(false)}
        onConfirm={handleConfirmDeactivate}
        title={t("users.deactivate_confirm_title")}
        description={t("users.deactivate_confirm_description", { email: userToDeactivate?.email })}
        confirmText={t("users.deactivate")}
        cancelText={t("common.cancel")}
        isLoading={isDeactivating}
        type="danger"
        documentHeight={documentHeight}
      />
    </>
  );
}
